import React, { createContext, useContext, useMemo } from 'react'
import type {
  Feature,
  FeatureToggle,
  FeatureTogglesContextValue,
  FeatureTogglesProviderProps,
  FeatureValue,
} from './types'
import {
  findFeatureToggleValueAsync,
  getEnvironment,
  mapFeatureToggles,
  DEFAULT_FEATURE_TOGGLE_VALUES,
} from './utils'

export const FeatureTogglesContext = createContext<
  FeatureTogglesContextValue | undefined
>(undefined)

export const useFeatureTogglesContext = (): FeatureTogglesContextValue => {
  const context = useContext(FeatureTogglesContext)
  if (!context) {
    throw new Error(
      'useFeatureToggles must be used within a FeatureTogglesProvider'
    )
  }

  return context
}

export const FeatureTogglesProvider = ({
  children,
  configuration,
  httpGet,
}: FeatureTogglesProviderProps) => {
  const featureTogglesPromise = useMemo<Promise<FeatureToggle[]>>(() => {
    return httpGet(
      `${configuration.urlBase}config/${
        configuration.platform
      }/${getEnvironment()}`
    )
      .then((res) => mapFeatureToggles(res.data.configurations))
      .catch((err) => {
        console.error('Feature toggles request failed', err)
        return Object.entries(DEFAULT_FEATURE_TOGGLE_VALUES).map(
          ([key, value]) => ({
            key: key as Feature,
            value,
          })
        )
      })
  }, [configuration.urlBase, configuration.platform, httpGet])

  return (
    <FeatureTogglesContext.Provider
      value={{
        featureTogglesPromise: featureTogglesPromise,
      }}
    >
      {children}
    </FeatureTogglesContext.Provider>
  )
}

export const useIsFeatureEnabled = (
  feature: Feature
): (() => Promise<FeatureValue>) => {
  const context = useFeatureTogglesContext()

  return () =>
    findFeatureToggleValueAsync(context.featureTogglesPromise, feature)
}

/**
 * HoC to be used with legacy React Class Components
 */
export const withFeatureToggles = <P extends object>(
  WrappedComponent: React.ComponentType<
    P & { findFeatureToggleValue: (feature: Feature) => Promise<FeatureValue> }
  >
) => {
  const WithFeatureTogglesComponent = (props: P) => {
    const context = useFeatureTogglesContext()

    return (
      <WrappedComponent
        {...props}
        findFeatureToggleValue={(feature: Feature): Promise<FeatureValue> =>
          findFeatureToggleValueAsync(context.featureTogglesPromise, feature)
        }
      />
    )
  }

  return WithFeatureTogglesComponent
}

export default FeatureTogglesProvider
